import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authService, userService } from '../services/api';

// Query keys
export const authKeys = {
  all: ['auth'],
  validateToken: () => [...authKeys.all, 'validate-token'],
  user: () => [...authKeys.all, 'user'],
};

// Hook for validating token and getting user data
export const useValidateToken = (enabled = true) => {
  return useQuery({
    queryKey: authKeys.validateToken(),
    queryFn: async () => {
      const response = await authService.validateToken();
      return response.data;
    },
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      if (error.response?.status === 401) {
        localStorage.clear();
        window.location.href = '/signin';
      }
    },
  });
};

// Hook for getting current user data
export const useCurrentUser = () => {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async () => {
      const response = await userService.getUserByToken();
      return response.data.user;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
  });
};

// Hook for signing out
export const useSignOut = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authService.signOut,
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
      localStorage.clear();
      window.location.href = '/signin';
    },
    onError: (error) => {
      console.error('Sign out error:', error);
      // Still clear local data even if API call fails
      queryClient.clear();
      localStorage.clear();
      window.location.href = '/signin';
    },
  });
};

// Hook for updating user profile
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userService.updateProfile,
    onSuccess: (data) => {
      // Update user cache
      queryClient.setQueryData(authKeys.user(), data.data.user);
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};

// Hook for uploading profile image
export const useUploadProfileImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userService.uploadProfileImage,
    onSuccess: (data) => {
      // Update user cache
      queryClient.setQueryData(authKeys.user(), data.data.user);
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
}; 